import { useLanguage } from "@/context/LanguageContext";
import SectionLabel from "@/components/SectionLabel";
import {
  ShieldCheck,
  SlidersHorizontal,
  History,
  Hand,
  Check,
  Edit3,
  X,
  Minus,
} from "lucide-react";

const POINT_ICONS = [SlidersHorizontal, ShieldCheck, History];

function StateCell({ state, legend }) {
  if (state === "on") {
    return (
      <div className="flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-emerald-500/12 border border-emerald-500/20">
        <Check size={11} strokeWidth={2.5} className="text-emerald-300" />
        <span className="text-[10.5px] font-medium text-emerald-300 hidden sm:inline">
          {legend.on}
        </span>
      </div>
    );
  }
  if (state === "validate") {
    return (
      <div className="flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-amber-500/12 border border-amber-500/20">
        <ShieldCheck size={11} strokeWidth={2} className="text-amber-300" />
        <span className="text-[10.5px] font-medium text-amber-300 hidden sm:inline">
          {legend.validate}
        </span>
      </div>
    );
  }
  if (state === "scoped") {
    return (
      <div className="flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-sky-500/12 border border-sky-500/20">
        <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />
        <span className="text-[10.5px] font-medium text-sky-300 hidden sm:inline">
          {legend.scoped}
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center px-2 py-1.5 rounded-lg bg-white/[0.04] border border-white/8">
      <Minus size={11} className="text-white/30" />
    </div>
  );
}

export default function Security() {
  const { t } = useLanguage();
  const columns = t("security.permissions_columns");
  const matrix = t("security.permissions_matrix");
  const legend = t("security.legend");
  const points = t("security.points");

  return (
    <section
      id="security"
      data-testid="security-section"
      className="relative overflow-hidden bg-[#1A1814] text-white"
    >
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div
          className="absolute top-[-15%] left-[-10%] w-[560px] h-[560px] opacity-25"
          style={{
            background:
              "radial-gradient(circle, rgba(67,125,234,0.5) 0%, transparent 70%)",
            filter: "blur(60px)",
          }}
        />
      </div>

      <div className="container-iko relative py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left — copy */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <SectionLabel icon={Hand} onDark>{t("security.eyebrow")}</SectionLabel>
            <h2 className="font-display font-semibold text-4xl lg:text-[64px] leading-[0.98] tracking-[-0.034em] text-balance mb-6">
              <span className="block">{t("security.title_l1")}</span>
              <span className="block text-white/55">{t("security.title_l2")}</span>
            </h2>
            <p className="text-base lg:text-lg text-white/65 leading-relaxed max-w-md mb-10">
              {t("security.subtitle")}
            </p>

            <div className="border-t border-white/10 pt-7">
              <p className="font-display font-semibold text-xl lg:text-2xl leading-[1.15] tracking-[-0.02em] text-balance">
                <span className="block text-white">{t("security.hero_phrase_l1")}</span>
                <span className="block text-white/55 mt-1">{t("security.hero_phrase_l2")}</span>
              </p>
            </div>
          </div>

          {/* Right — big permissions dashboard with validation overlay */}
          <div className="lg:col-span-7 relative">
            <div
              data-testid="security-permissions"
              className="rounded-3xl bg-white/[0.03] border border-white/10 backdrop-blur p-5 lg:p-7"
            >
              <div className="flex items-center justify-between mb-5 pb-4 border-b border-white/8">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-white/[0.06] border border-white/10 flex items-center justify-center">
                    <ShieldCheck size={13} strokeWidth={1.8} className="text-[#8F9FFE]" />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/60 font-semibold">
                    Permissions
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">
                    actif
                  </span>
                </div>
              </div>

              {/* Table header */}
              <div className="grid grid-cols-6 gap-2 mb-3 px-1">
                {columns.map((c, i) => (
                  <div
                    key={i}
                    className={`font-mono text-[9.5px] uppercase tracking-[0.18em] text-white/45 font-semibold ${
                      i === 0 ? "col-span-2" : "text-center"
                    }`}
                  >
                    {c}
                  </div>
                ))}
              </div>

              {/* Table rows */}
              <div className="space-y-2">
                {matrix.map((row, i) => (
                  <div
                    key={i}
                    data-testid={`perm-row-${i}`}
                    className="grid grid-cols-6 gap-2 items-center px-1 py-1.5 rounded-xl hover:bg-white/[0.03] transition-colors"
                  >
                    <div className="col-span-2 font-display font-bold text-[14px] text-white tracking-tight">
                      {row.tool}
                    </div>
                    {row.states.map((s, j) => (
                      <StateCell key={j} state={s} legend={legend} />
                    ))}
                  </div>
                ))}
              </div>

              {/* Legend */}
              <div className="mt-5 pt-4 border-t border-white/8 flex flex-wrap items-center gap-x-5 gap-y-2">
                <div className="flex items-center gap-1.5">
                  <Check size={10} strokeWidth={3} className="text-emerald-400" />
                  <span className="text-[10.5px] text-white/55">{legend.on}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <ShieldCheck size={10} strokeWidth={2} className="text-amber-400" />
                  <span className="text-[10.5px] text-white/55">{legend.validate}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />
                  <span className="text-[10.5px] text-white/55">{legend.scoped}</span>
                </div>
              </div>
            </div>

            {/* Validation overlay — top-right, safe from header */}
            <div
              data-testid="security-validation"
              className="absolute top-4 -right-3 lg:-right-6 w-[260px] z-10"
            >
              <div className="relative">
                <div
                  className="absolute -inset-3 rounded-3xl opacity-50 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(67,125,234,0.45) 0%, transparent 70%)",
                    filter: "blur(18px)",
                  }}
                />
                <div className="relative rounded-2xl bg-[#FAFAF7] text-[#1A1814] p-4 border border-[#437DEA]/40 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.6)]">
                  <div className="flex items-center justify-between mb-2.5">
                    <div className="inline-flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#6B6862] font-semibold">
                        {t("security.validation_title")}
                      </span>
                    </div>
                    <button className="text-[#94918A] hover:text-[#1A1814] transition-colors">
                      <X size={12} />
                    </button>
                  </div>
                  <div className="text-[13px] font-medium text-[#1A1814] leading-snug mb-3">
                    {t("security.validation_body")}
                  </div>
                  <div className="flex gap-1.5">
                    <button className="flex-1 inline-flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-full border border-[#F0EBE0] text-[11px] font-medium text-[#6B6862] hover:bg-[#F4F2EC] transition-colors">
                      <Edit3 size={10} /> {t("security.validation_modify")}
                    </button>
                    <button className="flex-1 inline-flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-full bg-[#1A1814] text-white text-[11px] font-medium hover:bg-[#437DEA] transition-colors">
                      <Check size={10} strokeWidth={2.5} /> {t("security.validation_confirm")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 3 control points */}
        <div className="mt-16 lg:mt-20 grid grid-cols-1 sm:grid-cols-3 gap-5">
          {points.map((p, i) => {
            const Icon = POINT_ICONS[i] || ShieldCheck;
            return (
              <div
                key={i}
                data-testid={`security-point-${i}`}
                className="rounded-2xl bg-white/[0.03] border border-white/10 p-6 backdrop-blur hover:bg-white/[0.05] transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-white/[0.06] border border-white/10 flex items-center justify-center text-[#8F9FFE] mb-4">
                  <Icon size={17} strokeWidth={1.8} />
                </div>
                <h3 className="font-display font-bold text-lg tracking-[-0.018em] mb-2 text-white">
                  {p.title}
                </h3>
                <p className="text-[13px] leading-relaxed text-white/60">{p.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
